import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Wind, Sparkles, Brain, Heart, Baby, Leaf } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BeneficiosPage = () => {
  const [content, setContent] = useState({});
  const [contentLoaded, setContentLoaded] = useState(false);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await axios.get(`${API}/content`);
        const pageContent = {};
        response.data.forEach(item => {
          if (item.page === 'beneficios') {
            pageContent[item.section] = item;
          }
        });
        setContent(pageContent);
      } catch (error) {
      } finally {
        setContentLoaded(true);
      }
    };
    fetchContent();
  }, []);

  const heroContent = content.hero || {};
  const respiratoryContent = content.respiratory || {};
  const skinContent = content.skin || {};
  const nervousContent = content.nervous || {};

  const benefitCategories = [
    {
      icon: Wind,
      title: 'Sistema Respiratorio',
      color: 'halo-brown',
      benefits: [
        'Favorece la limpieza de las vías respiratorias',
        'Fluidifica la mucosidad',
        'Reduce la inflamación leve de las vías aéreas',
        'Mejora la ventilación pulmonar',
        'Apoyo en alergia estacional y rinitis',
        'Complemento en bronquitis recurrente'
      ]
    },
    {
      icon: Sparkles,
      title: 'Piel y Sistema Cutáneo',
      color: 'halo-sage',
      benefits: [
        'Propiedades antiinflamatorias naturales',
        'Beneficios en piel atópica',
        'Apoyo en casos de dermatitis',
        'Mejora en psoriasis leve',
        'Propiedades antibacterianas',
        'Ambiente seco favorable para la piel'
      ]
    },
    {
      icon: Brain,
      title: 'Sistema Nervioso',
      color: 'halo-mauve',
      benefits: [
        'Reducción del estado de alerta constante',
        'Facilitación de estados de calma',
        'Regulación más estable',
        'Descanso más profundo',
        'Apoyo en ansiedad leve',
        'Reducción de la sobrecarga sensorial'
      ]
    }
  ];

  const conditions = [
    { name: 'Alergia estacional', icon: Leaf },
    { name: 'Rinitis', icon: Wind },
    { name: 'Bronquitis leve', icon: Wind },
    { name: 'Congestión', icon: Wind },
    { name: 'Ansiedad', icon: Brain },
    { name: 'Estrés', icon: Brain },
    { name: 'Piel atópica', icon: Sparkles },
    { name: 'Psoriasis', icon: Sparkles },
    { name: 'Rosácea', icon: Sparkles },
    { name: 'Fatiga mental', icon: Brain },
    { name: 'Alta sensibilidad', icon: Heart },
    { name: 'Recuperación respiratoria', icon: Wind }
  ];

  return (
    <div className="bg-halo-cream" data-testid="beneficios-page">
      {/* Hero */}
      <section className="relative py-24 bg-halo-charcoal overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          {contentLoaded && heroContent.image_url && (
            <img
              src={heroContent.image_url}
              alt=""
              className="w-full h-full object-cover"
            />
          )}
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <span className="text-halo-brown font-medium tracking-widest text-sm">
              ENFOQUE INTEGRAL
            </span>
            <h1 className="font-heading text-5xl md:text-6xl text-halo-cream mt-4 mb-6">
              Beneficios de la Haloterapia
            </h1>
            <p className="text-halo-cream/80 text-lg leading-relaxed">
              Respiración, piel y sistema nervioso: un enfoque integral del bienestar 
              basado en las propiedades naturales de la sal mineral.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Benefits */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-3 gap-8">
            {benefitCategories.map((category, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.15 }}
                viewport={{ once: true }}
                className="bg-halo-surface rounded-2xl p-8 border border-halo-taupe/10"
              >
                <div className={`w-16 h-16 rounded-full bg-${category.color}/10 flex items-center justify-center mb-6`}>
                  <category.icon size={32} className={`text-${category.color}`} />
                </div>
                <h3 className="font-heading text-2xl text-halo-charcoal mb-6">
                  {category.title}
                </h3>
                <ul className="space-y-4">
                  {category.benefits.map((benefit, j) => (
                    <li key={j} className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-halo-brown mt-2 flex-shrink-0" />
                      <span className="text-halo-text text-sm">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Conditions */}
      <section className="py-24 bg-halo-surface">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-4xl text-halo-charcoal mb-4">
              Puede resultar especialmente útil como complemento en
            </h2>
            <p className="text-halo-text max-w-2xl mx-auto">
              La haloterapia actúa como apoyo natural en diversas condiciones
            </p>
          </motion.div>

          <div className="flex flex-wrap justify-center gap-4">
            {conditions.map((condition, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.05 }}
                viewport={{ once: true }}
                className="flex items-center gap-2 bg-halo-cream px-5 py-3 rounded-full border border-halo-taupe/10"
              >
                <condition.icon size={16} className="text-halo-brown" />
                <span className="text-halo-charcoal text-sm font-medium">{condition.name}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              {contentLoaded && respiratoryContent.image_url && (
                <img
                  src={respiratoryContent.image_url}
                  alt="Sesión de haloterapia"
                  className="rounded-2xl w-full h-[450px] object-cover"
                />
              )}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <h2 className="font-heading text-4xl text-halo-charcoal">
                ¿Cómo funciona?
              </h2>
              <p className="text-halo-text leading-relaxed text-lg">
                Cuando se respira en un ambiente seco y controlado con micropartículas 
                de sal, puede contribuir a mejorar la ventilación pulmonar, reducir 
                la carga inflamatoria y favorecer una respiración más profunda y eficiente.
              </p>
              <p className="text-halo-text leading-relaxed">
                La sal posee propiedades antiinflamatorias, antibacterianas, mucolíticas 
                (ayuda a fluidificar mucosidad) e higroscópicas (atrae humedad, favoreciendo 
                la limpieza de vías respiratorias).
              </p>
              <p className="text-halo-text leading-relaxed">
                La mejora de la respiración está directamente vinculada con la regulación 
                del sistema nervioso. Respirar mejor ayuda a disminuir la activación 
                simpática (estado de alerta) y facilita estados de calma.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-16 bg-halo-brown/10">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="space-y-4"
          >
            <h3 className="font-heading text-2xl text-halo-charcoal">
              Importante
            </h3>
            <p className="text-halo-text leading-relaxed">
              La haloterapia no sustituye tratamientos médicos, pero puede actuar como 
              complemento natural dentro de un estilo de vida saludable. Siempre consulta 
              con tu médico antes de iniciar cualquier terapia complementaria, especialmente 
              si tienes condiciones médicas existentes.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-halo-charcoal">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <h2 className="font-heading text-4xl text-halo-cream">
              Descubre los beneficios por ti mismo
            </h2>
            <p className="text-halo-cream/70 max-w-xl mx-auto">
              Reserva tu primera sesión y experimenta cómo la haloterapia puede 
              contribuir a tu bienestar.
            </p>
            <Link
              to="/bonos"
              className="inline-flex items-center gap-2 bg-halo-brown text-halo-cream px-8 py-4 rounded-full font-medium hover:bg-halo-charcoal transition-colors"
              data-testid="beneficios-reservar-btn"
            >
              Comprar bono
              <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default BeneficiosPage;
